import type { ReactNode } from 'react';
import { sentenceCase } from 'change-case';
import { Pill, type PillProps, type PillVariant } from '@interxfi/design-system/components/pill/pill';
import type { IxcPortfolioV1TransactionDetail, IxcRaglV1TransactionDetail, IxfAccountingV1TransactionDetail } from '@interxfi/types/generated/api';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { MutualFundsFundOrderActionPill } from '../mutual-funds/fund-order/mutual-funds-fund-order-action-pill';
import { OrderSidePill } from '../order/order-side-pill';
import type { TransactionSource } from './utils';

interface TransactionTypePillProps extends Omit<PillProps, 'children' | 'variant'> {
  detail: IxcPortfolioV1TransactionDetail | IxcRaglV1TransactionDetail | IxfAccountingV1TransactionDetail | undefined;
  source: TransactionSource;
}

export function TransactionTypePill({ isPending, detail, source, ...props }: TransactionTypePillProps) {
  let variant: PillVariant = 'neutral';
  let content: ReactNode = null;
  let node: ReactNode = null;

  switch (detail?.['!type']) {
    case 'exc.jpmam.v1.MoneyMarketTransaction':
      content = 'Money Market';
      variant = 'quaternary';
      break;
    case 'ixc.billing.v1.ChargeTransaction':
      content = 'Billing Charge';
      variant = 'danger';
      break;
    case 'ixc.billing.v1.CustomerChargeTransaction':
      content = source === 'ragl' ? 'Billing Contra' : 'Billing Income';
      variant = 'green';
      break;
    case 'ixc.funding.v1.FundingTransaction':
      content = 'Funding';
      variant = 'tertiary';
      break;
    case 'ixc.funding.v1.FundingBatchTransaction':
      content = 'Batch Funding';
      variant = 'tertiary';
      break;
    case 'ixc.trade.v1.NettingIndicationTransaction':
      content = 'Netting Indication';
      variant = 'neutral';
      break;
    case 'ixc.orders.v1.TradeTransaction':
      node = <OrderSidePill isPending={isPending} side={detail.value.side} {...props} />;
      break;
    case 'ixf.accounting.v1.ManualEntryTransaction':
      content = 'Manual Entry';
      variant = 'secondary';
      break;
    case 'ixc.portfolio.v1.DividendPaymentTransaction':
      content = 'Dividend';
      variant = 'quaternary';
      break;
    case 'ixc.sweeps.clearing.v1.SweepsTransaction':
      content = 'Sweep';
      variant = 'quaternary';
      break;
    case 'ixc.ragl.v1.ManualTransaction':
      content = 'Manual Entry';
      variant = 'primary';
      break;
    case 'ixc.corpactions.v1.CorpactionTransaction':
      content = 'Corporate Action';
      variant = 'tertiary';
      break;
    case 'ixc.corpactions.caadmin.v1.DTCPaymentTransaction':
      content = 'DTC Payment';
      variant = 'tertiary';
      break;
    case 'ixc.orders.v1.BatchAllocationAvgPriceTransaction':
    case 'ixc.orders.v1.BatchAllocationCustomerTransaction':
      content = 'Batch Allocation';
      variant = 'neutral';
      break;
    case 'ixc.orders.v1.BatchRemainderInventoryTransaction':
      node = <OrderSidePill isPending={isPending} side={detail.value.side} {...props} />;
      break;
    case 'ixc.inventory.v1.InventoryTradeTransaction':
      node = <OrderSidePill isPending={isPending} side={detail.value.trade.side} {...props} />;
      break;
    case 'ixc.inventory.v1.SettleTradingDayTransaction':
      content = 'Settlement';
      variant = 'neutral';
      break;
    case 'ixc.inventory.v1.CloseTradingDayTransaction':
      content = 'Day Close';
      variant = 'neutral';
      break;
    case 'ixc.dtcc.cns.v1.SettlementTransaction':
      content = 'CNS';
      variant = 'neutral';
      break;
    case 'ixc.mutualfunds.v1.FundTransaction':
      node = <MutualFundsFundOrderActionPill action={detail.value.action} isPending={isPending} {...props} />;
      break;
    case undefined:
      break;
    default:
      exhaustiveCheck(detail);
      content = sentenceCase(detail?.['!type'])?.split('.').pop() || 'Unknown Transaction';
  }

  return (
    node || (
      <Pill isPending={!content || isPending} {...props} variant={variant}>
        {content}
      </Pill>
    )
  );
}
